'use client';

import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Calendar as CalendarIcon, RotateCcw } from 'lucide-react';
import * as React from 'react';
// Pinned to `9.13.2` (not `^`) across every consuming package.json: `9.14.0`
// pulls in unnecessary transitive dependencies otherwise
import {
  type CustomComponents,
  type DateRange,
  dateMatchModifiers,
  DayPicker,
  getDefaultClassNames,
  useDayPicker,
} from 'react-day-picker';

import { cn } from '@heidi/utils';
import { getDateFnsLocale, getGregorianCalendarLocale } from '@heidi/utils/dates';

import { Button } from '../button';
// Package-internal seam, deliberately not on the button door — see `../button/index.ts`.
import { useButtonProps } from '../button/use-button-props';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

import { CalendarChevron } from './calendar-chevron';
import {
  calendarCaptionLabelClasses,
  calendarDayButtonClasses,
  calendarDayCellClasses,
  calendarDisabledClasses,
  calendarDropdownClasses,
  calendarDropdownRootClasses,
  calendarDropdownsClasses,
  calendarFooterClasses,
  calendarHiddenClasses,
  calendarMonthCaptionClasses,
  calendarMonthClasses,
  calendarMonthGridClasses,
  calendarMonthsClasses,
  calendarNavButtonClasses,
  calendarNavClasses,
  calendarOutsideClasses,
  calendarRangeEndClasses,
  calendarRangeMiddleClasses,
  calendarRangeStartClasses,
  calendarRootClasses,
  calendarRootTokensClasses,
  calendarTodayClasses,
  calendarWeekdayClasses,
  calendarWeekNumberCellClasses,
  calendarWeekNumberClasses,
  calendarWeekNumberHeaderClasses,
} from './calendar.styles';
import type {
  CalendarDayButtonProps,
  CalendarFooterProps,
  CalendarProps,
  CalendarTodayButtonProps,
  CalendarTodaySelectHandler,
} from './calendar.types';
import {
  isSameLocalMonth,
  monthWindowStartForToday,
  selectToday,
  startOfLocalDay,
} from './select-today';

/** Stable defaults — `getDefaultClassNames()` is pure and does not depend on props. */
const defaultClassNames = getDefaultClassNames();

const CalendarRoot = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { rootRef?: React.Ref<HTMLDivElement> }
>(({ className, rootRef, ...props }, ref) => {
  const composedRef = useComposedRefs(ref, rootRef);
  return (
    <div data-slot="calendar" ref={composedRef} className={cn(className)} {...props} />
  );
});
CalendarRoot.displayName = 'CalendarRoot';

const CalendarWeekNumber = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<'td'>
>(({ children, ...props }, ref) => {
  return (
    <td ref={ref} {...props}>
      <div className={calendarWeekNumberCellClasses}>{children}</div>
    </td>
  );
});
CalendarWeekNumber.displayName = 'CalendarWeekNumber';

const CalendarFooter = React.forwardRef<HTMLDivElement, CalendarFooterProps>(
  (
    { className, showTodayButton, onReset, mode, today, disabled, onSelect, ...props },
    ref
  ) => {
    const intl = useDesignSystemIntl();

    return (
      <div
        ref={ref}
        data-slot="calendar-footer"
        className={cn(calendarFooterClasses, className)}
        {...props}
      >
        {showTodayButton ? (
          <CalendarTodayButton
            mode={mode}
            today={today}
            disabled={disabled}
            onSelect={onSelect}
          />
        ) : null}
        {onReset ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            data-testid="calendar-reset"
            onClick={onReset}
          >
            <RotateCcw aria-hidden="true" />
            {intl.formatMessage({
              defaultMessage: 'Reset',
              id: 'BMKIz1C+7E',
              description: 'Clears the selected date or date range on the calendar',
            })}
          </Button>
        ) : null}
      </div>
    );
  }
);
CalendarFooter.displayName = 'CalendarFooter';

const CalendarTodayButton = React.forwardRef<HTMLButtonElement, CalendarTodayButtonProps>(
  ({ mode, today, disabled, onSelect }, ref) => {
    const intl = useDesignSystemIntl();
    const { goToMonth, isSelected, months, select } = useDayPicker<{
      mode: 'multiple';
    }>();
    const isDisabled = disabled != null && dateMatchModifiers(today, disabled);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      const firstDisplayed = months[0]?.date;
      const windowStart = monthWindowStartForToday(today, months.length);
      if (!firstDisplayed || !isSameLocalMonth(firstDisplayed, windowStart)) {
        goToMonth(windowStart);
      }

      if (mode === 'multiple') {
        if (!isSelected?.(today)) {
          select?.(today, {}, event);
        }
        return;
      }
      if (mode === 'range') {
        (onSelect as ((value: DateRange | undefined) => void) | undefined)?.(
          selectToday('range', today)
        );
        return;
      }
      (onSelect as ((value: Date | undefined) => void) | undefined)?.(
        selectToday('single', today)
      );
    };

    return (
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        size="xs"
        disabled={isDisabled}
        data-testid="calendar-today"
        onClick={handleClick}
      >
        <CalendarIcon aria-hidden="true" />
        {intl.formatMessage({
          defaultMessage: 'Today',
          id: 'kizSECoJR2',
          description: 'Jumps the calendar to today and selects that day',
        })}
      </Button>
    );
  }
);
CalendarTodayButton.displayName = 'CalendarTodayButton';

const Calendar = React.memo(
  React.forwardRef<HTMLDivElement, CalendarProps>(function Calendar(
    {
      className,
      classNames,
      showOutsideDays = true,
      captionLayout = 'label',
      buttonVariant = 'ghost',
      formatters,
      components,
      monthsLayout = 'row',
      showTodayButton = false,
      onReset,
      'data-testid': testId,
      ...props
    },
    ref
  ) {
    const { locale: userLocale } = useDesignSystemIntl();
    const dateFnsLocale = getDateFnsLocale(userLocale);
    const dateFormatterLocale = getGregorianCalendarLocale(userLocale);
    // The nav buttons are classNames on a `DayPicker` slot rather than rendered
    // `Button`s, so they need the same recipe resolution `Button` does internally
    // or they stay pinned to the stable recipe under the forms flag (DS-1575).
    //
    // Class string only — DayPicker's `classNames` map takes no attributes, so the
    // `data-slot` / `data-experimental-*` markers can't ride along here.
    // `buttonVariant` is caller-supplied, so this must resolve the tone override
    // too: a success-toned nav button would otherwise keep the legacy fill.
    const { className: navButtonClasses } = useButtonProps({
      variant: buttonVariant,
      size: 'xs',
    });
    // Re-read every render rather than memoising on `props.today`: with no
    // `today` prop that dependency never changes, so a picker left mounted
    // across midnight keeps highlighting yesterday. `today` is passed to
    // DayPicker unconditionally below, which replaces its own per-render
    // `dateLib.today()`, so this has to roll over the same way. The memo key is
    // the day, not the `Date` identity, so a fresh `new Date()` each render
    // doesn't churn it.
    const resolvedToday = props.today ?? new Date();
    const todayKey = `${resolvedToday.getFullYear()}-${resolvedToday.getMonth()}-${resolvedToday.getDate()}`;
    const today = React.useMemo(
      () => startOfLocalDay(resolvedToday),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the day above, deliberately not on the Date identity
      [todayKey]
    );
    const canShowToday =
      showTodayButton &&
      (props.mode === 'single' || props.mode === 'multiple' || props.mode === 'range');
    const showFooter = Boolean(canShowToday || onReset);
    const hostOnSelect =
      'onSelect' in props
        ? (props.onSelect as CalendarTodaySelectHandler | undefined)
        : undefined;
    // Today writes through the host's `onSelect`. With no `onSelect` the host
    // is uncontrolled, DayPicker owns the selection, and the only way in is its
    // `select()` — which in range mode runs `addToRange` and *extends* the
    // current range back to today instead of restarting it there. So own the
    // selection here for that case: Today then means the same thing controlled
    // or not, and day clicks keep working because DayPicker is driven from the
    // same state.
    const ownsSelection =
      canShowToday &&
      hostOnSelect == null &&
      (props.mode === 'single' || props.mode === 'range');
    const [internalSelected, setInternalSelected] = React.useState<
      Date | DateRange | undefined
    >(() =>
      'selected' in props ? (props.selected as Date | DateRange | undefined) : undefined
    );
    const onSelect = ownsSelection
      ? (setInternalSelected as CalendarTodaySelectHandler)
      : hostOnSelect;
    // Merged rather than spread as a second override: DayPicker's props are a
    // union discriminated on `mode`, and a second spread makes TypeScript
    // distribute both sides into a cross product with mismatched `selected`
    // arms. `ownsSelection` already narrows mode to single or range, so the
    // pair is correct by construction.
    const dayPickerProps = ownsSelection
      ? ({
          ...props,
          selected: internalSelected,
          onSelect: setInternalSelected,
        } as unknown as typeof props)
      : props;

    return (
      <DayPicker
        showOutsideDays={showOutsideDays}
        locale={dateFnsLocale}
        className={cn(calendarRootTokensClasses, className)}
        captionLayout={captionLayout}
        formatters={{
          formatMonthDropdown: (date) =>
            date.toLocaleString(dateFormatterLocale, { month: 'short' }),
          formatCaption: (date) =>
            date.toLocaleString(dateFormatterLocale, { month: 'long', year: 'numeric' }),
          ...formatters,
        }}
        classNames={{
          root: cn(calendarRootClasses, defaultClassNames.root),
          months: cn(calendarMonthsClasses(monthsLayout), defaultClassNames.months),
          // `w-fit` so the month shrinks to the fixed 7×`--cell-size` grid instead
          // of stretching columns when a parent is wider than the calendar.
          month: cn(calendarMonthClasses, defaultClassNames.month),
          nav: cn(calendarNavClasses, defaultClassNames.nav),
          button_previous: cn(
            navButtonClasses,
            calendarNavButtonClasses,
            defaultClassNames.button_previous
          ),
          button_next: cn(
            navButtonClasses,
            calendarNavButtonClasses,
            defaultClassNames.button_next
          ),
          month_caption: cn(calendarMonthCaptionClasses, defaultClassNames.month_caption),
          dropdowns: cn(calendarDropdownsClasses, defaultClassNames.dropdowns),
          dropdown_root: cn(calendarDropdownRootClasses, defaultClassNames.dropdown_root),
          dropdown: cn(calendarDropdownClasses, defaultClassNames.dropdown),
          caption_label: cn(
            calendarCaptionLabelClasses(captionLayout),
            defaultClassNames.caption_label
          ),
          month_grid: cn(calendarMonthGridClasses, defaultClassNames.month_grid),
          weekdays: cn(defaultClassNames.weekdays),
          weekday: cn(calendarWeekdayClasses, defaultClassNames.weekday),
          week: cn(defaultClassNames.week),
          week_number_header: cn(
            calendarWeekNumberHeaderClasses,
            defaultClassNames.week_number_header
          ),
          week_number: cn(calendarWeekNumberClasses, defaultClassNames.week_number),
          day: cn(calendarDayCellClasses, defaultClassNames.day),
          // `day_button` is omitted on purpose: `CalendarDayButton` already
          // layers `defaultClassNames.day_button`. Setting it here would pass
          // the same string in as `props.className` and apply it twice.
          range_start: cn(calendarRangeStartClasses, defaultClassNames.range_start),
          range_middle: cn(calendarRangeMiddleClasses, defaultClassNames.range_middle),
          range_end: cn(calendarRangeEndClasses, defaultClassNames.range_end),
          today: cn(calendarTodayClasses, defaultClassNames.today),
          outside: cn(calendarOutsideClasses, defaultClassNames.outside),
          disabled: cn(calendarDisabledClasses, defaultClassNames.disabled),
          hidden: cn(calendarHiddenClasses, defaultClassNames.hidden),
          ...classNames,
        }}
        components={{
          // DayPicker types slots as `(props) => JSX.Element`; our forwarded /
          // memo wrappers return `ReactNode`. Passed by identity so tests (and
          // `components` overrides) can still match the export.
          Chevron: CalendarChevron as CustomComponents['Chevron'],
          DayButton: CalendarDayButton as CustomComponents['DayButton'],
          WeekNumber: CalendarWeekNumber as CustomComponents['WeekNumber'],
          ...components,
          // After `...components`, so `Root` is the one slot a caller cannot
          // override. Deliberate, and no call site in the repo overrides it.
          Root: ({ children, ...rootProps }) => (
            <CalendarRoot {...rootProps} ref={ref} data-testid={testId}>
              {children}
              {showFooter ? (
                <CalendarFooter
                  showTodayButton={canShowToday}
                  onReset={onReset}
                  mode={props.mode}
                  today={today}
                  disabled={props.disabled}
                  onSelect={onSelect}
                />
              ) : null}
            </CalendarRoot>
          ),
        }}
        {...dayPickerProps}
        today={today}
      />
    );
  })
);
Calendar.displayName = 'Calendar';

/**
 * Day-cell control for DayPicker. A DS `Button` (`ghost` / `xs` / `iconOnly`)
 * with only the size box reset to `--cell-size` — chrome and the
 * `border-focus` ring stay on the recipe; type is caption (`text-xs`) at
 * regular weight.
 */
const CalendarDayButton = React.memo(
  React.forwardRef<HTMLButtonElement, CalendarDayButtonProps>(function CalendarDayButton(
    { className, day, modifiers, ...props },
    forwardedRef
  ) {
    const internalRef = React.useRef<HTMLButtonElement>(null);
    const ref = useComposedRefs(forwardedRef, internalRef);
    React.useEffect(() => {
      if (modifiers.focused) internalRef.current?.focus();
    }, [modifiers.focused]);

    return (
      <Button
        {...props}
        ref={ref}
        type="button"
        variant="ghost"
        size="xs"
        iconOnly
        data-day={day.date.toLocaleDateString()}
        data-selected-single={
          modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_end &&
          !modifiers.range_middle
        }
        data-range-start={modifiers.range_start}
        data-range-end={modifiers.range_end}
        data-range-middle={modifiers.range_middle}
        className={cn(calendarDayButtonClasses, defaultClassNames.day_button, className)}
      />
    );
  })
);
CalendarDayButton.displayName = 'CalendarDayButton';

export { Calendar, CalendarDayButton };
